/**
 * Steady-state performance analysis over the same aero and powerplant models
 * the integrator uses. Two customers: aircraft-spec.ts calibrates the drag
 * polar and the power-lapse exponent through these functions, and the
 * acceptance tests (AD-007f) check the resulting envelopes against the
 * manual's tables.
 *
 * Everything here is level-flight/excess-power analysis — it deliberately does
 * not step the sim, so a disagreement between an envelope computed here and one
 * flown by `step` is a real finding, not a tautology.
 */

import { dragCoefficient, stallSpeedMps, type AeroSpec } from "./aero.ts";
import { densityRatioAt, standardAtmosphere, type AirProperties } from "./atmosphere.ts";
import { availableShaftPowerW, thrustN, type PropulsionSpec } from "./engine-model.ts";
import { GRAVITY_MPS2, clamp } from "./units.ts";

export interface AirframePerformance {
  readonly massKg: number;
  readonly aero: AeroSpec;
}

export interface PoweredPerformance extends AirframePerformance {
  readonly propulsion: PropulsionSpec;
}

const SPEED_SCAN_STEPS = 160;
const CEILING_SOLVER_STEPS = 40;

interface LevelFlightState {
  readonly alphaRad: number;
  readonly liftCl: number;
  readonly dynamicPressure: number;
}

/**
 * The closed-form level-flight relation `levelDragN` and the trim solver
 * below both need: the angle of attack (and lift coefficient) that puts lift
 * exactly on weight at `airspeedMps`. `null` when the wing cannot hold the
 * aircraft up at that speed (below stall for this mass/altitude).
 */
function levelFlightState(
  airframe: AirframePerformance,
  air: AirProperties,
  airspeedMps: number,
): LevelFlightState | null {
  const { aero } = airframe;
  const weightN = airframe.massKg * GRAVITY_MPS2;
  const dynamicPressure = 0.5 * air.densityKgM3 * airspeedMps * airspeedMps;
  if (dynamicPressure <= 0) return null;
  const liftCl = weightN / (dynamicPressure * aero.wingAreaM2);
  if (liftCl > aero.clMax) return null;
  return { alphaRad: liftCl / aero.clAlphaPerRad, liftCl, dynamicPressure };
}

/**
 * Drag in level flight at a given speed, or `Infinity` when the wing cannot
 * hold the aircraft up at that speed (below stall).
 */
export function levelDragN(airframe: AirframePerformance, air: AirProperties, airspeedMps: number): number {
  const state = levelFlightState(airframe, air, airspeedMps);
  if (state === null) return Number.POSITIVE_INFINITY;
  // Zero sideslip: this solves *symmetric* level flight, which is what the
  // ratio law and the trim solve are defined on (AD-007i's slip drag is real
  // but has no place in a wings-level, ball-centred steady state).
  return (
    state.dynamicPressure *
    airframe.aero.wingAreaM2 *
    dragCoefficient(airframe.aero, state.liftCl, state.alphaRad, 0)
  );
}

/**
 * Angle of attack that puts lift exactly on weight in level flight at
 * `airspeedMps` — the trim solve `createSim`'s `trimmed` option uses so a
 * hands-off start is genuine level cruise rather than the small steady
 * descent an untrimmed 0°-pitch start settles into. `NaN` when the requested
 * speed cannot be trimmed level (see `levelFlightState`).
 */
export function trimAlphaRad(airframe: AirframePerformance, air: AirProperties, airspeedMps: number): number {
  return levelFlightState(airframe, air, airspeedMps)?.alphaRad ?? Number.NaN;
}

/**
 * Throttle (0..1) that puts thrust exactly on drag in level flight at
 * `airspeedMps`, using the same altitude lapse and propeller model `step`
 * applies — the other half of the trim solve alongside `trimAlphaRad`. `NaN`
 * under the same conditions `trimAlphaRad` is.
 */
export function trimThrottle(powered: PoweredPerformance, air: AirProperties, airspeedMps: number): number {
  const dragN = levelDragN(powered, air, airspeedMps);
  if (!Number.isFinite(dragN)) return Number.NaN;
  const shaftPowerW = availableShaftPowerW(powered.propulsion, air.densityRatio);
  const effectiveSpeed = Math.max(airspeedMps, powered.propulsion.staticThrustSpeedMps);
  const requiredPowerW = (dragN * effectiveSpeed) / powered.propulsion.propellerEfficiency;
  return clamp(requiredPowerW / Math.max(shaftPowerW, 1e-6), 0, 1);
}

export function levelPowerRequiredW(
  airframe: AirframePerformance,
  air: AirProperties,
  airspeedMps: number,
): number {
  return levelDragN(airframe, air, airspeedMps) * airspeedMps;
}

function scanSpeeds(airframe: AirframePerformance, air: AirProperties): readonly number[] {
  const stall = stallSpeedMps(airframe.aero, airframe.massKg, air.densityKgM3);
  const low = stall * 1.02;
  const high = stall * 6;
  return Array.from(
    { length: SPEED_SCAN_STEPS },
    (_unused, index) => low + ((high - low) * index) / (SPEED_SCAN_STEPS - 1),
  );
}

/** Least power any level flight speed needs — the quantity that sets the ceiling. */
export function minimumLevelPowerW(airframe: AirframePerformance, air: AirProperties): number {
  let best = Number.POSITIVE_INFINITY;
  for (const speed of scanSpeeds(airframe, air)) {
    best = Math.min(best, levelPowerRequiredW(airframe, air, speed));
  }
  return best;
}

/** Fastest speed at which thrust still equals drag in level flight, by bisection. */
export function maxLevelSpeedMps(powered: PoweredPerformance, air: AirProperties): number {
  const shaftPowerW = availableShaftPowerW(powered.propulsion, air.densityRatio);
  const excess = (speed: number): number =>
    thrustN(powered.propulsion, shaftPowerW, speed) - levelDragN(powered, air, speed);

  const stall = stallSpeedMps(powered.aero, powered.massKg, air.densityKgM3);
  let low = stall * 1.05;
  let high = stall * 8;
  if (excess(low) <= 0) return low;
  if (excess(high) > 0) return high;
  for (let i = 0; i < CEILING_SOLVER_STEPS; i++) {
    const mid = (low + high) / 2;
    if (excess(mid) > 0) low = mid;
    else high = mid;
  }
  return (low + high) / 2;
}

/**
 * Best rate of climb from excess power. Negative above the ceiling, which is
 * exactly what the ceiling solver bisects on.
 */
export function bestClimbRateMps(powered: PoweredPerformance, air: AirProperties): number {
  const shaftPowerW = availableShaftPowerW(powered.propulsion, air.densityRatio);
  const weightN = powered.massKg * GRAVITY_MPS2;
  let best = Number.NEGATIVE_INFINITY;
  for (const speed of scanSpeeds(powered, air)) {
    const drag = levelDragN(powered, air, speed);
    if (!Number.isFinite(drag)) continue;
    const excessPowerW = thrustN(powered.propulsion, shaftPowerW, speed) * speed - drag * speed;
    best = Math.max(best, excessPowerW / weightN);
  }
  return best;
}

/** Best climb rate at an altitude, wrapping the standard atmosphere. */
export function climbRateAtAltitude(powered: PoweredPerformance, altitudeM: number): number {
  return bestClimbRateMps(powered, standardAtmosphere(altitudeM));
}

/** Altitude at which best climb rate reaches zero — the absolute ceiling the lapse produces. */
export function absoluteCeilingM(powered: PoweredPerformance): number {
  const highest = 15000;
  if (climbRateAtAltitude(powered, 0) <= 0) return 0;
  if (climbRateAtAltitude(powered, highest) > 0) return highest;
  let low = 0;
  let high = highest;
  for (let i = 0; i < CEILING_SOLVER_STEPS; i++) {
    const mid = (low + high) / 2;
    if (climbRateAtAltitude(powered, mid) > 0) low = mid;
    else high = mid;
  }
  return (low + high) / 2;
}

/**
 * Solves the power-lapse exponent that puts zero excess power at `ceilingM`.
 * AD-007c wants the stored ceilings to emerge from the lapse rather than be
 * clamped; the density lapse alone leaves every aircraft flying well above its
 * stored figure, so the engine lapse carries the difference and is fitted here,
 * per aircraft, against the one ceiling number the data does give exactly.
 */
export function solvePowerLapseExponent(
  powered: PoweredPerformance,
  ceilingM: number,
  bounds: { readonly low: number; readonly high: number },
): number {
  const air = standardAtmosphere(ceilingM);
  if (densityRatioAt(ceilingM) >= 1) return bounds.low;
  const climbWith = (exponent: number): number =>
    bestClimbRateMps(
      { ...powered, propulsion: { ...powered.propulsion, powerLapseExponent: exponent } },
      air,
    );

  if (climbWith(bounds.low) <= 0) return bounds.low;
  if (climbWith(bounds.high) > 0) return bounds.high;
  let low = bounds.low;
  let high = bounds.high;
  for (let i = 0; i < CEILING_SOLVER_STEPS; i++) {
    const mid = (low + high) / 2;
    if (climbWith(mid) > 0) low = mid;
    else high = mid;
  }
  return (low + high) / 2;
}
